'use client';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HpMarketingAdWithCards } from '@pickleballinc/features/src/global/cards/hp-marketing-ad-with-cards';
import { LeagueCardv2 } from '@pickleballinc/features/src/global/cards/LeagueCardv2';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { Button } from '@pickleballinc/react-ui';
import { RenderAd } from '@pickleballinc/ui/components/advertisements/render-ad';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import type { HpMarketingAd } from '@pickleballinc/ui/types/hp-marketing-ad';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { useLeagueFavorite } from '@/modules/league/hooks/useFavoriteToggle';
import { useLeagueCardHref } from '@/modules/league/hooks/useLeaguesCardHref';
import { LeagueSearch } from '@/modules/league/new-components/types';
import { FavoriteLeagueCard } from '@/modules/league/search-page/FavoriteLeagueCard';
import { LeaguesListSkeleton } from '@/modules/league/search-page/LeagueListSkeleton';
import { ZONE_IDS_PL } from '@/types/ads.types';
import { PLACEMENT_IDS_PL } from '@/types/freestar.pl.types';
import { SystemModuleType } from '@/types/types';
import { getLeaguesSearchPageData } from '@/utils/actions/leagues/search/getLeaguesSearchPageData';
import { FEATURE_FLAGS, IS_PRODUCTION } from '@/utils/helpers/constants';
import { LEAGUES_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

interface LeaguesListProps {
  ipAddress: string;
  initialData: { leagues: LeagueSearch[]; totalRecords: number };
  leaguesTotalRecords: number;
  /**
   * Search card ads for the LEAGUE surface. When non-empty, the first ad rotator occupies the first grid
   * cell ahead of the league cards. Empty array = nothing rendered, no space held.
   */
  searchCardAds?: HpMarketingAd[];
}

const NewLeagueCard = ({ league }: { league: LeagueSearch }) => {
  const { href, isExternal } = useLeagueCardHref(league, false);

  return (
    <LeagueCardv2
      id={league.LeagueID}
      slug={league.slug}
      dateFrom={league.FirstMatchDate ? new Date(league.FirstMatchDate).toISOString() : undefined}
      dateTo={league.LastMatchDate ? new Date(league.LastMatchDate).toISOString() : undefined}
      startTime={
        league.CurMatchDateStartDateTime
          ? `Start: ${formatDateTime(league.CurMatchDateStartDateTime, { type: 'time' })}`
          : ''
      }
      title={league.LeagueTitle}
      location={formatLocation([league.City, league.StateAbbreviation, league.CountryAbbreviation])}
      currency={league.PaymentCurrencyAbbreviation}
      price={league.Cost_Registration_Member}
      formatTag={league.PaymentCurrencyFormatTag}
      logo={
        league.LeagueLogo || league.ClubLogo
          ? `${publicEnv.NEXT_PUBLIC_PBRACKETS_CDN_URL}/uploads/clubs/${league.ClubID}/${league.LeagueLogo || league.ClubLogo}`
          : LEAGUES_PLACEHOLDER_IMAGE
      }
      registrationCount={league.TotalRegistered}
      isLocked={league.IsLocked}
      isRegistrationClosed={league.IsRegClosed === 1}
      clubTitle={league.ClubTitle}
      leaguesAppBaseURL={publicEnv.NEXT_PUBLIC_PL_URL}
      customCardHref={href}
      openInNewTab={isExternal}
      useFavoriteState={useLeagueFavorite}
      showFavoriteIcon
      initialIsFavorite={league.isFavorite}
    />
  );
};

const LeagueListItem = ({ league }: { league: LeagueSearch }) =>
  FEATURE_FLAGS.ENABLE_NEW_CARDS ? (
    <NewLeagueCard league={league} />
  ) : (
    <FavoriteLeagueCard league={league} isManageLeague={false} />
  );

export const LeaguesList = ({
  ipAddress,
  initialData,
  leaguesTotalRecords,
  searchCardAds = [],
}: LeaguesListProps) => {
  const [numberOfDifferentBannerImages, setNumberOfDifferentBannerImages] = useState<number>();
  const searchParams = useSearchParams();

  const filters = {
    keyword: searchParams?.get('keyword') ?? '',
    status: searchParams?.get('status') ?? 'active',
    country: searchParams?.get('country') ?? '',
    state: searchParams?.get('state') ?? '',
    player_groups: '',
    formats: searchParams?.get('formats') ?? '',
    weekdays_parameter: searchParams?.get('weekdays_parameter') ?? '',
    weekdays_value: searchParams?.get('weekdays_value') ?? '',
    page: searchParams?.get('page') ?? '',
    clubId: searchParams?.get('clubId') ?? '',
  };

  const keyword = filters.keyword;
  const status = filters.status;
  const country = filters.country;
  const state = filters.state;
  const player_groups = filters.player_groups;
  const formats = filters.formats;
  const weekdays_parameter = filters.weekdays_parameter;
  const weekdays_value = filters.weekdays_value;
  const page = filters.page;
  const clubId = filters.clubId;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'leagues',
      keyword,
      status,
      country,
      state,
      player_groups,
      formats,
      weekdays_parameter,
      weekdays_value,
      page,
      clubId,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const newSearchParams = new URLSearchParams(searchParams || '');

      let status = newSearchParams.get('status');
      let page = newSearchParams.get('page');

      if (!status) {
        status = 'active';
        newSearchParams.set('status', status);
      }

      if (!page) {
        page = '1';
        newSearchParams.set('page', page);
      }
      newSearchParams.set('page', pageParam.toString());
      const leagues = await getLeaguesSearchPageData({ params: newSearchParams.toString() });

      return {
        page: pageParam,
        data: leagues.data,
      };
    },
    initialData: {
      pages: [
        {
          page: 1,
          data: {
            leagues: initialData?.leagues || [],
            totalRecords: initialData?.totalRecords || 0,
          },
        },
      ],
      pageParams: [undefined],
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getNextPageParam: (page) => {
      const totalPages = Math.ceil(page.data.totalRecords / 20);
      return page.page + 1 <= totalPages ? page.page + 1 : undefined;
    },
  });

  const [columns, setColumns] = useState<number | null>(null);

  useEffect(() => {
    const getColumns = () => {
      const width = window.innerWidth;
      if (width >= 1280) return 4;
      if (width >= 1024) return 3;
      if (width >= 768) return 2;
      return 1;
    };

    const handleResize = () => {
      setColumns(getColumns());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 👇 show loader while window is undefined
  if (columns === null)
    return (
      <div className='flex flex-col items-center gap-2'>
        <div className='flex w-full max-w-screen-xl flex-col items-center gap-4 p-4 sm:pt-0 2xl:px-0'>
          <div className='flex w-full flex-col gap-2'>
            <LeaguesListSkeleton />
          </div>
        </div>
      </div>
    );

  const firstPage = data?.pages[0];
  const leagues = firstPage?.data?.leagues ?? [];
  const recordCount = leagues[0]?.RecordCount ?? 0;

  const totalResults = `${recordCount} results found`;
  const totalFilteredResults = `${leaguesTotalRecords} ${
    filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
  } ${LEAGUE_LABELS.plural} `;

  const hasNoLeagues = leagues.length === 0;
  // HP Marketing search card ad replaces the legacy 2-column Freestar `HOUSE_LEAGUES_CARD` slot. When
  // active, the ad takes one grid cell at the start of the first row, so we fill the rest of that row
  // with `columns` leagues (vs. `columns - 1` under the old 2-col ad). When inactive, the first grid is
  // skipped entirely and all leagues flow into the second grid below.
  const isAdActive = searchCardAds.length > 0;
  const toSkip = isAdActive ? columns : 0;
  const leaguesToRenderFirstRow = leagues.slice(0, toSkip);
  // Per-page balance — keep every page at 20 visible cells (4 clean rows at xl / 5 cols):
  //   - With HP ad: 1 ad cell + 19 leagues = 20 cells. Carry 1 league via the page-stitching prepend
  //     (`slice(-1)`) so page 2+ also display 1 stitched + 19 new = 20 leagues.
  //   - Without HP ad: the legacy 18/+2-carry split assumed the 2-column Freestar filled those 2 slots.
  //     With no ad at all, show 20 leagues per page and carry nothing — otherwise the last row of page 1
  //     would be 2 cells short.
  const pageSliceEnd = isAdActive ? 19 : 20;
  const carryCount = isAdActive ? 1 : 0;
  const laterPages = data?.pages.slice(1) ?? [];
  /**
   * A page holds back its last `carryCount` league(s) so the NEXT page can prepend them and still
   * total 20 cells. Only the genuinely last loaded page renders all 20 — it has no successor to carry
   * into. This must be decided per page: `hasNextPage` is query-global, so using it directly made the
   * final fetch retroactively widen page 1's slice from 19 to 20, which both broke the row alignment
   * and rendered the carried league twice.
   */
  const isLastLoadedPage = (pageIndex: number) => pageIndex === laterPages.length && !hasNextPage;
  const leaguesToRenderRestOfFirstPage = leagues.slice(
    toSkip,
    isLastLoadedPage(0) ? 20 : pageSliceEnd
  );

  const additionalLeaguesFromNextPages =
    laterPages.flatMap((page, pageIndex) => {
      // `slice(-0)` returns the whole array, so guard the carry-0 case explicitly.
      const lastLeagueFromPrevious =
        carryCount > 0 ? (data.pages[pageIndex]?.data.leagues.slice(-carryCount) ?? []) : [];
      const remainingLeagues = page.data.leagues.slice(
        0,
        isLastLoadedPage(pageIndex + 1) ? 20 : pageSliceEnd
      );

      const allLeagues = [...(lastLeagueFromPrevious || []), ...remainingLeagues];

      return allLeagues.map((league, index) => {
        const isLast = index === allLeagues.length - 1;

        return (
          league && (
            <Fragment key={`league-ad-${league.LeagueID}`}>
              <LeagueListItem league={league} />
              {isLast ? (
                <RenderAd
                  placementId={PLACEMENT_IDS_PL.incontent_728_reusable}
                  zoneId={ZONE_IDS_PL.PROG_LEAGUES_BANNER}
                  pageId={100008}
                  place={pageIndex + 1}
                  ipAddress={ipAddress}
                  containerClassname='col-span-full w-full mx-0.5 sm:mx-auto'
                  numberOfDifferentZoneImages={numberOfDifferentBannerImages}
                  setNumberOfDifferentZoneImages={setNumberOfDifferentBannerImages}
                  isProd={IS_PRODUCTION}
                />
              ) : null}
            </Fragment>
          )
        );
      });
    }) ?? null;

  return (
    <>
      <div className='container mx-auto flex flex-col items-baseline pb-4 gap-2 2xl:px-0'>
        <div className='font-semibold text-gray-900 text-lg sm:text-xl'>{totalResults}</div>
        <div className='font-semibold text-sm'>{`${totalFilteredResults} Worldwide`}</div>
      </div>

      {hasNoLeagues ? (
        <div className='mt-6 flex w-full items-center justify-center sm:mt-8'>
          <NoDataFound
            title={`No ${LEAGUE_LABELS.pluralLower} found`}
            subtitle='Try changing or removing some of your filters or adjusting your search area.'
          />
        </div>
      ) : isAdActive ? (
        <div className='mx-auto !max-w-7xl grid grid-cols-1 min-[500px]:grid-cols-2 gap-3 p-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5 2xl:px-0'>
          <HpMarketingAdWithCards
            key='hp-marketing-search-card-ad'
            ads={searchCardAds}
            moduleFor={SystemModuleType.SEARCH_CARD_AD_MODULE_TYPE}
            useV2Cards={FEATURE_FLAGS.ENABLE_NEW_CARDS}
            // Mirror `LeagueCard`'s width tokens so the ad's max visible width matches the league cards next
            // to it in the grid rather than stretching to the full column track. Forwarded to the outer
            // wrapper (grid item) inside `HpMarketingAd` where max-width overrides `justify-self: stretch`.
            className='min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]'
          />
          {leaguesToRenderFirstRow.map((league) => (
            <LeagueListItem key={`${league.LeagueID}-${league.LeagueTitle}`} league={league} />
          ))}
        </div>
      ) : null}

      <div className='mx-auto !max-w-7xl grid grid-cols-1 min-[500px]:grid-cols-2 gap-3 p-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5 2xl:px-0'>
        {leaguesToRenderRestOfFirstPage.map((league, index) => (
          <Fragment key={`league-list-fragment-${league.LeagueID}`}>
            <LeagueListItem league={league} />
            {/*
             * Periodic full-width Freestar banner (`PROG_LEAGUES_BANNER`) sits at the end of the first-page
             * slice. Originally pinned to `index === 18 - columns`, which assumed the legacy 2-column
             * Freestar took two cells in the first grid. With the new 1-column HP marketing ad (and the
             * skipped-first-grid case), the slice length differs — anchor the banner to the last item of
             * the slice so it always renders at the bottom of the first page.
             */}
            {index === leaguesToRenderRestOfFirstPage.length - 1 && (
              <RenderAd
                placementId={PLACEMENT_IDS_PL.incontent_728_reusable}
                zoneId={ZONE_IDS_PL.PROG_LEAGUES_BANNER}
                pageId={100008}
                place={0}
                ipAddress={ipAddress}
                containerClassname='col-span-full grid w-full mx-0.5 sm:mx-auto'
                numberOfDifferentZoneImages={numberOfDifferentBannerImages}
                setNumberOfDifferentZoneImages={setNumberOfDifferentBannerImages}
                isProd={IS_PRODUCTION}
              />
            )}
          </Fragment>
        ))}

        {additionalLeaguesFromNextPages}
      </div>

      {hasNextPage ? (
        <div className='my-12 flex items-center justify-center'>
          <Button
            variant='secondary'
            disabled={!hasNextPage || isFetchingNextPage}
            aria-disabled={!hasNextPage || isFetchingNextPage}
            onClick={async () => {
              fetchNextPage();
            }}
          >
            {isFetchingNextPage ? 'Loading more...' : 'Load more'}
            <FontAwesomeIcon
              icon={isFetchingNextPage ? faSpinner : faArrowRight}
              className='ml-2'
              spin={isFetchingNextPage}
            />
          </Button>
        </div>
      ) : null}
    </>
  );
};
